import { routes } from '@/routes';
import { isSafeInternalPath } from '@/utils/url';

export const BOOT_FALLBACK_COOKIE = 'rvl_boot_fallback';

/** The only two values the middleware acts on. Anything else falls through to the ordinary path. */
export const BOOT_FALLBACK_KINDS = ['lost', 'stalled'] as const;

export type BootFallbackKind = (typeof BOOT_FALLBACK_KINDS)[number];

// Sized to the one navigation that sets it. A wider window lets a `lost` value survive into an
// unrelated load of '/' and expire an access token that was never rejected.
export const BOOT_FALLBACK_MAX_AGE_SECONDS = 5;

/**
 * Narrows an untrusted cookie value to a fallback kind.
 *
 * @param value The raw cookie value, or nothing when the request carried no cookie.
 * @returns True when the value is one of the two literals the middleware branches on.
 */
export const isBootFallbackKind = (value: string | null | undefined): value is BootFallbackKind =>
	typeof value === 'string' && (BOOT_FALLBACK_KINDS as readonly string[]).includes(value);

/**
 * Builds the root URL the fallback navigation targets.
 *
 * @param intendedPath The path the member asked for.
 * @returns '/' on its own when the path is unsafe or already the root, otherwise '/' carrying the
 * path as `redirectTo`.
 */
export const buildFallbackUrl = (intendedPath: string): string => {
	if (!isSafeInternalPath(intendedPath)) return routes.root;

	const { pathname, search } = new URL(intendedPath, 'http://localhost');
	if (pathname === routes.root) return routes.root;

	return `${routes.root}?redirectTo=${encodeURIComponent(`${pathname}${search}`)}`;
};

// The kind this document wrote. The marker itself is host-scoped at `Path=/`, so every tab in the
// jar reads the same value and it cannot identify which document set it.
let writtenKind: BootFallbackKind | null = null;

const navigateToFallback = (kind: BootFallbackKind): void => {
	writtenKind = kind;
	document.cookie = `${BOOT_FALLBACK_COOKIE}=${kind}; Path=/; Max-Age=${BOOT_FALLBACK_MAX_AGE_SECONDS}; SameSite=Lax; Secure`;
	// A document navigation fires the host window's unsaved-changes handler, and declining its
	// prompt leaves the member on the screen this is recovering them from.
	window.onbeforeunload = null;
	window.location.replace(
		buildFallbackUrl(`${window.location.pathname}${window.location.search}`)
	);
};

/**
 * Sends the member to the login form, asserting to the middleware that the session is gone.
 *
 * Uses a document navigation rather than the router so the middleware's `Set-Cookie` headers
 * apply: the middleware expires `sk_access_token`, which is HttpOnly and so unreachable from
 * script.
 */
export const markSessionLost = (): void => navigateToFallback('lost');

/**
 * Sends the member to '/' after a boot that never resolved, leaving every cookie in place.
 *
 * Nothing has been proven about the session, so the auth MFE mounts and renders its own error
 * screen once its probe gives up.
 *
 * Does nothing once this document has asserted a confirmed loss: `window.location.replace` cancels
 * no pending timer, so the overlay backstop can still fire while that navigation is in flight,
 * and downgrading the marker to `stalled` would leave the refused `sk_access_token` in the jar.
 * Another tab's `lost` does not suppress it — the marker is shared, so it says nothing about this
 * document, and suppressing on it would strand that tab under a released overlay.
 */
export const markBootStalled = (): void => {
	if (writtenKind === 'lost') return;
	navigateToFallback('stalled');
};
